import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { PRODUCTIONS, enrichProduction, imageForProduction } from "../data/reviews";

/**
 * Vertical productions timeline.
 * - Left rail: sticky year label with a hairline film-strip vertical spine
 * - Right column: clipped filmstrip image (parallax) + editorial title / meta
 * - Alternating layout every other row
 */
function TimelineRow({ prod, index }) {
  const rowRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const oddRow = index % 2 === 1;
  const p = enrichProduction(prod);
  const img = imageForProduction(prod.slug);

  return (
    <article
      ref={rowRef}
      data-testid={`timeline-row-${prod.slug}`}
      className="grid grid-cols-12 gap-x-4 md:gap-x-8 border-t border-white/10 py-10 md:py-16"
    >
      {/* Sticky year column */}
      <div className="col-span-3 md:col-span-2">
        <div className="sticky-year">
          <div className="font-display text-4xl md:text-6xl leading-none text-white/90">
            {prod.year}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40 mt-3">
            Chapter {prod.chapter}
          </div>
        </div>
      </div>

      {/* Editorial text */}
      <div
        className={`col-span-9 md:col-span-6 ${
          oddRow ? "md:order-3" : ""
        } flex flex-col justify-center`}
      >
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#c9a961] mb-4">
          {prod.genre} · {prod.venue}
        </div>
        <Link
          to={`/productions/${prod.slug}`}
          className="group inline-block"
          data-testid={`timeline-link-${prod.slug}`}
        >
          <h3 className="font-display text-3xl md:text-5xl leading-[0.95] uppercase tracking-tight text-white group-hover:text-[#c9a961] transition-colors">
            {prod.title}
          </h3>
        </Link>
        <p className="mt-6 text-white/70 text-base md:text-lg max-w-lg leading-relaxed">
          {prod.synopsis}
        </p>
        {p.hero_quote && (
          <blockquote className="mt-8 border-l-2 border-[#8b0000] pl-4 max-w-lg">
            <div className="italic font-display text-lg md:text-xl leading-snug text-white/90">
              &ldquo;{p.hero_quote}&rdquo;
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] font-mono text-white/50">
              — {p.hero_outlet}
            </div>
          </blockquote>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.2em] font-mono text-white/50">
          <span>{p.review_count} reviews</span>
          {p.rave_count > 0 && (
            <>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <span>{p.rave_count} raves</span>
            </>
          )}
          {p.average_rating && (
            <>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <span>{p.average_rating}/5 avg</span>
            </>
          )}
          <Link
            to={`/productions/${prod.slug}`}
            className="link-hover text-white ml-auto md:ml-0"
          >
            Explore →
          </Link>
          <a
            href={prod.official_url}
            target="_blank"
            rel="noreferrer"
            className="link-hover text-white/60 hover:text-white"
            data-testid={`timeline-official-${prod.slug}`}
          >
            On colabtheatre.co.uk ↗
          </a>
        </div>
      </div>

      {/* Filmstrip image */}
      <div
        className={`col-span-12 md:col-span-4 mt-8 md:mt-0 ${
          oddRow ? "md:order-2" : ""
        }`}
      >
        <Link to={`/productions/${prod.slug}`} className="block">
          <div className="filmstrip">
            <motion.img
              src={img}
              alt={`${prod.title} — Best immersive theatre in London. COLAB Theatre immersive theatre production still, ${prod.year}, ${prod.venue}.`}
              style={{ y: yImg }}
              loading="lazy"
            />
          </div>
        </Link>
      </div>
    </article>
  );
}

export default function Timeline() {
  return (
    <section
      data-testid="home-timeline"
      className="section relative bg-[#050505]"
    >
      <div className="px-6 md:px-14 lg:px-20 xl:px-24 mb-16 md:mb-24 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-white/50 mb-6">
            COLAB Immersive Theatre Productions · London · 2014 → 2026
          </div>
          <h2 className="bleed-h max-w-4xl text-white">
            Fifteen immersive theatre productions.
            <span className="block text-white/40">
              Fourteen years in London.
            </span>
          </h2>
        </div>
        <div className="hidden md:block text-right text-[10px] uppercase tracking-[0.3em] font-mono text-white/40 max-w-xs">
          Every COLAB immersive theatre show in London, in order, with the best
          reviews of each production.
        </div>
      </div>

      <div className="px-6 md:px-14 lg:px-20 xl:px-24 border-b border-white/10">
        {PRODUCTIONS.map((p, i) => (
          <TimelineRow key={p.slug} prod={p} index={i} />
        ))}
      </div>
    </section>
  );
}
